import type { ExerciseRow } from '@gymtv/data';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { COLS, type Col, type DayState } from '../lib/editor';
import { NameCell } from './NameCell';

const HEAD: Record<Col, string> = { name: 'Exercise', sets: 'Sets', reps: 'Reps', weight: 'Weight', rest_s: 'Rest s', notes: 'Notes' };
const MODE: Partial<Record<Col, 'numeric' | 'decimal'>> = { sets: 'numeric', reps: 'numeric', weight: 'decimal', rest_s: 'numeric' };

type Props = {
  day: DayState;
  units: string;
  /** The exercise library, for name autocomplete. */
  exercises: ExerciseRow[];
  onCell(row: number, col: Col, value: string): void;
  onAddRow(at: number): void;
  onRemoveRow(row: number): void;
  onMoveRow(from: number, to: number): void;
  onRename(name: string): void;
  onRemoveDay?: () => void;
  navigate(key: string, pos: { row: number; col: number }, rows: number, caret: { atStart: boolean; atEnd: boolean }): { row: number; col: number } | null;
};

/**
 * Spreadsheet-style grid for one day. Keyboard: arrows move between cells, Enter moves down
 * (adds a row at the end), Alt+↑/↓ moves the row, Ctrl/⌘+Enter inserts a row below,
 * Ctrl/⌘+Backspace deletes the row. Rows can also be dragged by the ⠿ handle.
 */
export function DayGrid(p: Props) {
  const { day } = p;
  const table = useRef<HTMLTableElement>(null);
  const [focus, setFocus] = useState<{ row: number; col: number } | null>(null);
  const [drag, setDrag] = useState<{ from: number; over: number | null } | null>(null);
  const [name, setName] = useState(day.name);
  useEffect(() => setName(day.name), [day.name]);

  useEffect(() => {
    if (!focus) return;
    const el = table.current?.querySelector<HTMLInputElement>(`input[data-row="${focus.row}"][data-col="${focus.col}"]`);
    if (el) {
      el.focus();
      el.select();
    }
    setFocus(null);
  }, [focus, day.rows.length]);

  function onKey(e: KeyboardEvent<HTMLInputElement>, row: number, col: number) {
    const mod = e.metaKey || e.ctrlKey;
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      const to = e.key === 'ArrowUp' ? row - 1 : row + 1;
      if (to >= 0 && to < day.rows.length) {
        e.preventDefault();
        p.onMoveRow(row, to);
        setFocus({ row: to, col });
      }
      return;
    }
    if (mod && e.key === 'Enter') {
      e.preventDefault();
      p.onAddRow(row + 1);
      setFocus({ row: row + 1, col: 0 });
      return;
    }
    if (mod && e.key === 'Backspace') {
      e.preventDefault();
      p.onRemoveRow(row);
      setFocus({ row: Math.max(0, Math.min(row, day.rows.length - 2)), col });
      return;
    }
    if (e.key === 'Enter' && row === day.rows.length - 1) {
      e.preventDefault();
      p.onAddRow(day.rows.length);
      setFocus({ row: row + 1, col: 0 });
      return;
    }
    const input = e.currentTarget;
    const caret = { atStart: input.selectionStart === 0 && input.selectionEnd === 0, atEnd: input.selectionStart === input.value.length };
    const next = p.navigate(e.key, { row, col }, day.rows.length, caret);
    if (next) {
      e.preventDefault();
      setFocus(next);
    }
  }

  const statusPill =
    day.status === 'saved' ? <span className="pill ok">saved</span>
    : day.status === 'saving' ? <span className="pill warn">saving…</span>
    : day.status === 'invalid' ? <span className="pill bad">fix to save</span>
    : day.status === 'error' ? <span className="pill bad">not saved</span>
    : <span className="pill warn">editing</span>;

  return (
    <section className="day" aria-label={`Week ${day.week}, ${day.name}`}>
      <div className="day-head">
        <input
          className="day-name"
          aria-label="Day name (all weeks)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name.trim() !== day.name && p.onRename(name.trim())}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
        {statusPill}
        {p.onRemoveDay && (
          <button className="small ghost" title="Remove this day from every week" onClick={p.onRemoveDay}>
            Remove day
          </button>
        )}
      </div>
      <div className="grid-scroll">
        <table className="grid" ref={table}>
          <colgroup>
            <col />
            <col className="c-name" />
            <col className="c-num" />
            <col className="c-num" />
            <col className="c-num" />
            <col className="c-num" />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th aria-label="Reorder" />
              {COLS.map((c) => (
                <th key={c}>{c === 'weight' ? `${HEAD[c]} ${p.units}` : HEAD[c]}</th>
              ))}
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {day.rows.map((r, row) => (
              <tr
                key={r.key}
                className={(drag?.from === row ? 'dragging ' : '') + (drag && drag.over === row && drag.from !== row ? 'drop-before' : '')}
                onDragOver={(e) => {
                  if (!drag) return;
                  e.preventDefault();
                  if (drag.over !== row) setDrag({ ...drag, over: row });
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (drag && drag.from !== row) p.onMoveRow(drag.from, row);
                  setDrag(null);
                }}
              >
                <td
                  className="handle"
                  draggable
                  title="Drag to reorder (or Alt+↑/↓)"
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(row));
                    setDrag({ from: row, over: null });
                  }}
                  onDragEnd={() => setDrag(null)}
                >
                  ⠿
                </td>
                {COLS.map((c, col) => {
                  const common = {
                    'data-row': row,
                    'data-col': col,
                    'aria-label': `${HEAD[c]}, row ${row + 1}`,
                    className: day.badRow === row && day.error?.toLowerCase().includes(c === 'rest_s' ? 'rest' : c === 'name' ? 'name' : c) ? 'bad' : '',
                  };
                  return (
                    <td key={c}>
                      {c === 'name' ? (
                        <NameCell
                          {...common}
                          value={r.name}
                          exercises={p.exercises}
                          placeholder="Add exercise…"
                          onChange={(v) => p.onCell(row, c, v)}
                          onGridKey={(e) => onKey(e, row, col)}
                        />
                      ) : (
                        <input {...common} inputMode={MODE[c]} value={r[c]} onChange={(e) => p.onCell(row, c, e.target.value)} onKeyDown={(e) => onKey(e, row, col)} />
                      )}
                    </td>
                  );
                })}
                <td>
                  <button className="small ghost" aria-label={`Delete row ${row + 1}`} title="Delete row (Ctrl/⌘+Backspace)" onClick={() => p.onRemoveRow(row)}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="day-foot">
        <button className="small" onClick={() => { p.onAddRow(day.rows.length); setFocus({ row: day.rows.length, col: 0 }); }}>
          + Add exercise
        </button>
        <span className={day.error ? 'error' : 'hint'} role={day.error ? 'alert' : undefined}>
          {day.error ?? (
            <>
              <kbd>Enter</kbd> next row · <kbd>Alt</kbd>+<kbd>↑</kbd>/<kbd>↓</kbd> move · <kbd>⌘/Ctrl</kbd>+<kbd>⌫</kbd> delete
            </>
          )}
        </span>
      </div>
    </section>
  );
}
